import React, { useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ErrorMessage } from '@hookform/error-message';
import { HrRed, HrParagraph } from '@components/styles/hr-styles';
import Label from '../../checkbox/label';
import { DisplaySettingsContext } from '../../../postulation_form/store/display-settings';

const CheckboxContainer = styled.div`
  width: ${(props) => props.width || 'auto'};
  position: relative;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin: ${(props) => props.margin || '0px'};
`;

const CheckboxUi = styled.label`
  min-width: 16px;
  height: 16px;
  border-radius: 4px;
  position: relative;
  margin-bottom: 0;
  border: 1.5px solid ${(props) => (props.disabled ? '#b9b9b9' : props.borderColor || '#000000')};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};

  &:after {
    content: '';
    display: block;
    width: 7px;
    height: 4px;
    border-bottom: 2.5px solid #ffffff;
    border-left: 2.5px solid #ffffff;
    transform: rotate(-45deg) scale(0);
    transition: transform ease 0.25s;
    position: absolute;
    top: 4px;
    left: 3px;
    border-radius: 1px;
  }

  ${({ radio }) =>
    radio &&
    `
		border-radius: 50%;
		&:after{
			content: '';
			display: block;
			border: none;
			width: 12px;
			height: 12px;
			border-radius: 50%;
			background-color: #24BB72;
			top: 2px;
			left: 2px;
		}
	`}
`;

const InputCheckbox = styled.input`
  width: auto;
  opacity: 0.00000001;
  position: absolute;
  &:checked ~ label {
    border: 1.5px solid #24bb72;
    background-color: #24bb72;
    &:after {
      transform: rotate(-46deg) scale(1);
    }
    ${({ type }) =>
      type === 'radio' &&
      `
			background-color: transparent !important;
		`}
  }
`;

export const Checkbox = ({
  onChange,
  checked,
  label,
  name,
  validate,
  radio,
  refCustom,
  control,
  defaultChecked,
  margin,
  customLabel,
  formState,
  children,
  disabled
}) => {
  const checkboxRef = refCustom || useRef(null);
  const validateRegister = {};
  const [t] = useTranslation();
  const { errors } = formState;
  if (validate.required) {
    validateRegister.required = t(`general.error.obligatoryField`);
  }
  if (validate.TermsConditions) {
    validateRegister.required = t('postulant.terms_and_conditions.required');
  }
  const handleChecked = (e) => {
    if (onChange) onChange(e);
  };

  const { darkMode, fontSizeSetting } = useContext(DisplaySettingsContext);

  return (
    <>
      <CheckboxContainer margin={margin} width='100%'>
        <Controller
          control={control}
          name={name}
          defaultValue={defaultChecked}
          rules={validateRegister}
          render={({ field }) => (
            <>
              <InputCheckbox
                ref={refCustom || field.ref}
                type='checkbox'
                id={name}
                onChange={(e) => {
                  field.onChange(e.target.checked);
                  handleChecked(e);
                }}
                checked={field.value}
                name={name}
                value={field.value}
                aria-label='checkbox'
                disabled={disabled}
              />
              <CheckboxUi htmlFor={`${name}`} radio={radio} disabled={disabled} borderColor={darkMode ? 'white' : ''} />
              {label && (
                <Label
                  fontSizeSetting={fontSizeSetting}
                  onClick={() => checkboxRef.current.click()}
                  fontWeight='400'
                  color={darkMode ? 'white' : ''}
                  disabled={disabled}
                >
                  {label}
                </Label>
              )}
              {customLabel && children}
            </>
          )}
        />
      </CheckboxContainer>

      <ErrorMessage
        errors={errors}
        name={name}
        render={({ message }) => <HrParagraph color={HrRed}>{message}</HrParagraph>}
      />
    </>
  );
};

Checkbox.defaultProps = {
  customLabel: false,
  formState: { errors: {} },
  validate: {}
};

Checkbox.propTypes = {
  id: PropTypes.string,
  onChange: PropTypes.func,
  label: PropTypes.string,
  name: PropTypes.string,
  radio: PropTypes.bool,
  defaultChecked: PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.func]),
  checked: PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.func]),
  value: PropTypes.oneOfType([PropTypes.bool, PropTypes.string, PropTypes.number]),
  customLabel: PropTypes.bool,
  refCustom: PropTypes.oneOfType([PropTypes.object, PropTypes.bool, PropTypes.func]),
  formState: PropTypes.object,
  validate: PropTypes.object
};
